'use client';

import { motion, useInView } from 'motion/react';
import { useRef, useState, useMemo } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { isFinnishPage, WORK_FI, WORK_EN } from '../../../lib/blogPaths';
import { getWpImageSrc } from '../../../lib/wpMedia';

const img = (slug) => `/partnerships/${slug}.jpg`;

const FILTER_VALUES = ['All', 'Software Development', 'UI/UX Design', 'Healthcare', 'FinTech'];
const FILTER_LABELS_FI = ['Kaikki', 'Ohjelmistokehitys', 'UI/UX-suunnittelu', 'Terveydenhuolto', 'FinTech'];

const caseStudies = [
  {
    client: 'Mehiläinen',
    slug: 'mehilainen',
    caseSlug: 'mehilainen',
    title: 'Partnering to empower customer-centric digital healthcare',
    titleFi: 'Kumppanuudesta voimaa asiakaslähtöisen digitaalisen terveydenhuollon kehittämiseen',
    tags: 'Healthcare • Web Development • Long-term Partnership',
    categories: ['Healthcare', 'Software Development'],
  },
  {
    client: 'Visma Sirius',
    slug: 'visma',
    caseSlug: 'visma-sirius',
    title: 'New mobile app enhances customer experience',
    titleFi: 'Asiakaslähtöisyyteen potkua uuden mobiilisovelluksen myötä',
    tags: 'PropTech • Mobile Development • UX Design',
    categories: ['Software Development', 'UI/UX Design'],
  },
  {
    client: 'Smartum',
    slug: 'smartum',
    caseSlug: 'smartum',
    title: 'Customer understanding drives digital service development',
    titleFi: 'Asiakasymmärryksestä digitaalisen palvelukehityksen perusta',
    tags: 'HealthTech • User Research • Design',
    categories: ['Healthcare', 'UI/UX Design'],
  },
  {
    client: 'Helsinki',
    slug: 'helsinki',
    caseSlug: 'helsinki',
    title: 'Public sector digital services and platforms',
    tags: 'Public Sector • Software Development • UX Design',
    categories: ['Software Development', 'UI/UX Design'],
  },
  {
    client: 'Elisa',
    slug: 'elisa',
    caseSlug: 'elisa',
    title: 'Telecom and digital experience solutions',
    tags: 'Telecom • Software Development • Integration',
    categories: ['Software Development', 'FinTech'],
  },
];

/** Same three as etusivu: Mehiläinen, Visma Sirius, Smartum. Use for Palvelut "Tutustu tekemäämme työhön". */
export const FEATURED_CASE_STUDIES = caseStudies.slice(0, 3);

/**
 * @param {Array<{ client: string, title: string, titleFi?: string, slug: string, imageUrl?: string|null }>} [caseStudies] - When provided (e.g. FEATURED_CASE_STUDIES or 3 from API), show only these; no filter. Otherwise use hardcoded list with filter.
 */
export function ServicesCaseStudiesSection({ caseStudies: propCaseStudies = [] }) {
  const router = useRouter();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const isFinnish = isFinnishPage(router?.pathname) || router?.asPath === '/meista';
  const readCaseStudyLabel = isFinnish ? 'Lue asiakastarina' : 'Read Case Study';
  const [activeFilter, setActiveFilter] = useState('All');

  const useApiCases = Array.isArray(propCaseStudies) && propCaseStudies.length > 0;
  const filteredCases = useMemo(() => {
    if (useApiCases) return propCaseStudies.slice(0, 3);
    if (activeFilter === 'All') return caseStudies;
    return caseStudies.filter((cs) => cs.categories?.includes(activeFilter));
  }, [useApiCases, propCaseStudies, activeFilter]);

  const heading = isFinnish ? 'Tutustu tekemäämme työhön' : "See How We've Delivered";
  const viewAllHref = isFinnish ? WORK_FI : WORK_EN;
  const viewAllLabel = isFinnish ? 'Lisää kumppanuustarinoita' : 'View All Case Studies';
  const filterLabels = isFinnish ? FILTER_LABELS_FI : FILTER_VALUES;
  const caseStudyItemHref = (cs) => {
    if (useApiCases) return `${isFinnish ? WORK_FI : WORK_EN}/${cs.slug}`;
    return `/en/work/${cs.caseSlug ?? cs.slug}`;
  };

  return (
    <section ref={ref} className="section-rhythm bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-8"
        >
          <h2 className="heading-2 mb-4">{heading}</h2>
        </motion.div>

        {!useApiCases && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="flex flex-wrap justify-center gap-2 mb-10"
          >
            {FILTER_VALUES.map((value, index) => (
              <button
                key={value}
                type="button"
                onClick={() => setActiveFilter(value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all touch-manipulation ${
                  activeFilter === value
                    ? 'bg-[#0f172a] text-white'
                    : 'bg-gray-100 text-[#374151] hover:bg-gray-200'
                }`}
              >
                {filterLabels[index]}
              </button>
            ))}
          </motion.div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-10">
          {filteredCases.map((cs, index) => {
            const href = caseStudyItemHref(cs);
            const rawImageSrc = useApiCases && cs.imageUrl ? cs.imageUrl : img(cs.slug);
            const imageSrc = getWpImageSrc(rawImageSrc) || rawImageSrc;
            return (
              <motion.div
                key={useApiCases ? cs.slug : cs.client}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="group flex flex-col h-full bg-white rounded-2xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:shadow-[0_12px_40px_-10px_rgba(0,0,0,0.12)] transition-all duration-300"
              >
                <Link href={href} className="flex flex-col flex-1 touch-manipulation">
                  <div data-card-img className="rounded-t-2xl h-44 md:h-56 bg-gray-100 relative overflow-hidden shrink-0">
                    <Image
                      src={imageSrc}
                      alt={cs.client}
                      fill
                      sizes="(max-width: 768px) 100vw, 33vw"
                      className="object-cover rounded-t-2xl group-hover:scale-105 transition-transform duration-300 delay-75"
                      unoptimized
                      onError={(e) => {
                        const imgEl = e.target;
                        if (imgEl) imgEl.style.display = 'none';
                        const container = imgEl?.closest?.('[data-card-img]');
                        const fallback = container?.querySelector?.('[data-card-fallback]');
                        if (fallback) fallback.classList.remove('hidden');
                      }}
                    />
                    <div data-card-fallback className="absolute inset-0 hidden flex bg-navy items-center justify-center text-white font-semibold text-lg rounded-t-2xl">
                      {cs.client}
                    </div>
                  </div>
                  <div className="p-5 md:p-6 flex flex-col flex-1 flex-grow">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{cs.client}</p>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-4 leading-snug flex-1">{isFinnish && cs.titleFi ? cs.titleFi : cs.title}</h3>
                    <span className="inline-flex items-center gap-2 text-brand font-medium group-hover:gap-3 transition-all mt-auto">
                      {readCaseStudyLabel}
                      <ArrowRight className="w-5 h-5 shrink-0" aria-hidden />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center"
        >
          <Link
            href={viewAllHref}
            className="inline-flex items-center gap-2 text-brand font-semibold text-lg hover:gap-3 transition-all touch-manipulation"
          >
            {viewAllLabel}
            <ArrowRight className="w-5 h-5 shrink-0" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
